import React, { useEffect, useMemo, useCallback } from "react";
import { Box } from "@mui/material";
import {
  FormTextField,
  FormDatePicker,
} from "../../../../shared/components/redesign/forms";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  AccessTimeOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { formatUiDate } from "../../../../shared/utils/dateFormat";

// Locale-aware "LL" date format for the summary end date (global dayjs locale follows i18n).
dayjs.extend(localizedFormat);

// ─── Operations terracotta accent ──────────────────────────
const ACCENT = "#D4572B";
const ACCENT_SOFT = "rgba(212, 87, 43, 0.08)";
const ACCENT_GLOW = "rgba(212, 87, 43, 0.14)";
const ACCENT_BORDER = "rgba(212, 87, 43, 0.15)";

// ─── Paper-matched colors ──────────────────────────────────
const SECTION_LABEL = "#9CA3AF"; // muted gray for FREQUENCY / ENDS / NEXT OCCURRENCES
const TEXT_MUTED = "#6B7280";    // "Every … weeks on" text
const CARD_BG = "#FAFBFC";      // unselected end-card background
const CARD_BORDER = "#E5E7EB";  // unselected end-card border
const SELECTED_BG = "#D4572B0F"; // selected end-card bg (~6% opacity)
const SELECTED_BORDER = "#D4572B59"; // selected end-card border (~35%)
const SUMMARY_BG = "#D4572B0A"; // summary line bg
const SUMMARY_TEXT = "#B8451F";  // summary line text (darker terracotta)

interface RecurrenceRulePickerProps {
  value: string;
  onChange: (rrule: string) => void;
  startDate: string;
  previewDates?: string[];
}

type Frequency = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
type EndType = "never" | "count" | "date";

const DAY_CODES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;

const RecurrenceRulePicker: React.FC<RecurrenceRulePickerProps> = ({
  value,
  onChange,
  startDate,
  previewDates,
}) => {
  const { translate } = useTranslation("operations");

  // Parse existing RRULE or use defaults
  const parsed = useMemo(() => parseRRule(value), [value]);

  const [frequency, setFrequency] = React.useState<Frequency>(parsed.frequency);
  const [interval, setInterval] = React.useState<string>(parsed.interval.toString());
  const [endType, setEndType] = React.useState<EndType>(parsed.endType);
  const [endCount, setEndCount] = React.useState<string>(parsed.endCount.toString());
  const [endDate, setEndDate] = React.useState<Date | null>(parsed.endDate);
  const [selectedDays, setSelectedDays] = React.useState<string[]>(parsed.byDay);

  // Build RRULE from state
  const buildRRule = useCallback(() => {
    const parts: string[] = [`FREQ=${frequency}`];

    const intervalNum = parseInt(interval, 10);
    if (intervalNum > 1) {
      parts.push(`INTERVAL=${intervalNum}`);
    }

    if (frequency === "WEEKLY" && selectedDays.length > 0) {
      parts.push(`BYDAY=${selectedDays.join(",")}`);
    }

    if (endType === "count") {
      const count = parseInt(endCount, 10);
      if (count > 0) parts.push(`COUNT=${count}`);
    } else if (endType === "date" && endDate) {
      const dateStr = dayjs(endDate).format("YYYYMMDDTHHmmss") + "Z";
      parts.push(`UNTIL=${dateStr}`);
    }

    return parts.join(";");
  }, [frequency, interval, selectedDays, endType, endCount, endDate]);

  // Emit change when any input changes
  useEffect(() => {
    const newRule = buildRRule();
    if (newRule !== value) {
      onChange(newRule);
    }
  }, [frequency, interval, selectedDays, endType, endCount, endDate, buildRRule]);

  // Interval unit label — plural form follows the interval number typed next to it
  const unitLabel = useMemo(() => {
    const count = parseInt(interval, 10) || 1;
    switch (frequency) {
      case "DAILY": return translate("recurrence.unitDay", { count });
      case "WEEKLY": return translate("recurrence.unitWeek", { count });
      case "MONTHLY": return translate("recurrence.unitMonth", { count });
      case "YEARLY": return translate("recurrence.unitYear", { count });
      default: return "";
    }
  }, [frequency, interval, translate]);

  // End condition options (rendered inline — Paper-matched compact cards)
  const endCards: { code: EndType; label: string; icon: React.ReactNode }[] = useMemo(() => [
    {
      code: "never",
      label: translate("recurrence.never"),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.585 0-4.585 8 0 8 5.606 0 7.644-8 12.74-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
    {
      code: "count",
      label: translate("recurrence.afterN") || "After N",
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 9h16M4 15h16M10 3L8 21M16 3l-2 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
    {
      code: "date",
      label: translate("recurrence.onDate"),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
          <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      ),
    },
  ], [translate]);

  // Toggle a day in the selectedDays array
  const toggleDay = useCallback((code: string) => {
    setSelectedDays((prev) =>
      prev.includes(code) ? prev.filter((d) => d !== code) : [...prev, code]
    );
  }, []);

  // Sx that overrides CSS custom properties so shared form components
  // (FormSelect, FormDatePicker, FormTextField) resolve dt.blue → terracotta
  const terracottaScope = {
    "--accent": ACCENT,
    "--accent-hover": "#B8451F",
    "--accent-glow": ACCENT_GLOW,
    "--accent-soft": ACCENT_SOFT,
    "--accent-gradient-dark": "#B8451F",
    "--accent-gradient-light": "#E06B42",
  } as Record<string, string>;

  // Number inputs: mono font + terracotta focus + no spinner arrows
  const monoNumberSx = {
    "& input": {
      fontFamily: dt.fontMono,
      fontSize: "13px",
      fontWeight: 500,
      MozAppearance: "textfield",       // Firefox: hide spinners
      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
        WebkitAppearance: "none",        // Chrome/Safari/Edge: hide spinners
        margin: 0,
      },
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: dt.inputBg,
      "&:hover fieldset": { borderColor: ACCENT },
      "&.Mui-focused fieldset": { borderColor: ACCENT, borderWidth: "1.5px" },
    },
  };

  // Build human-readable summary line
  const summaryText = useMemo(() => {
    const intervalNum = parseInt(interval, 10) || 1;
    // One plural template per unit: "Every day" (count 1) / "Every {{count}} days"
    let text = "";

    switch (frequency) {
      case "DAILY":
        text = translate("recurrence.everyDay", { count: intervalNum });
        break;
      case "WEEKLY":
        text = translate("recurrence.everyWeek", { count: intervalNum });
        if (selectedDays.length > 0) {
          const dayLabels = selectedDays.map((d) => translate(`recurrence.daysOfWeek.${d}`));
          text += ` ${translate("recurrence.on") || "on"} ${dayLabels.join(", ")}`;
        }
        break;
      case "MONTHLY":
        text = translate("recurrence.everyMonth", { count: intervalNum });
        break;
      case "YEARLY":
        text = translate("recurrence.everyYear", { count: intervalNum });
        break;
    }

    if (endType === "never") {
      text += ` \u2014 ${translate("recurrence.noEndDate") || "no end date"}`;
    } else if (endType === "count") {
      const count = parseInt(endCount, 10) || 0;
      text += ` \u2014 ${translate("recurrence.occurrenceCount", { count })}`;
    } else if (endType === "date" && endDate) {
      text += ` \u2014 ${translate("recurrence.until") || "until"} ${dayjs(endDate).format("LL")}`;
    }

    return text;
  }, [frequency, interval, selectedDays, endType, endCount, endDate, translate]);

  // Frequency pill options
  const frequencyPills: { value: Frequency; label: string }[] = useMemo(() => [
    { value: "DAILY", label: translate("recurrence.daily") },
    { value: "WEEKLY", label: translate("recurrence.weekly") },
    { value: "MONTHLY", label: translate("recurrence.monthly") },
    { value: "YEARLY", label: translate("recurrence.yearly") },
  ], [translate]);

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: "14px",
      borderLeft: `2px solid #D4572B26`,
      marginLeft: "16px",
      paddingLeft: "22px",
      paddingRight: "22px",
      paddingTop: "4px",
      ...terracottaScope,
    }}>
      {/* ─── FREQUENCY section ───────────────────────────── */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
        <Box sx={{
          fontFamily: dt.font,
          fontSize: "10px",
          fontWeight: 700,
          color: SECTION_LABEL,
          textTransform: "uppercase",
          letterSpacing: "1px",
          lineHeight: "12px",
        }}>
          {translate("recurrence.frequency")}
        </Box>

        {/* Frequency pills */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
          {frequencyPills.map((pill) => {
            const isActive = frequency === pill.value;
            return (
              <Box
                key={pill.value}
                onClick={() => setFrequency(pill.value)}
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  fontWeight: isActive ? 600 : 500,
                  px: "14px",
                  py: "5px",
                  lineHeight: "14px",
                  borderRadius: "20px",
                  border: `1px solid ${isActive ? ACCENT : CARD_BORDER}`,
                  backgroundColor: isActive ? ACCENT : "#F4F6F9",
                  color: isActive ? "#FFFFFF" : TEXT_MUTED,
                  boxShadow: isActive ? `${ACCENT}40 0px 2px 6px` : "none",
                  cursor: "pointer",
                  userSelect: "none",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: ACCENT,
                    color: isActive ? "#FFFFFF" : ACCENT,
                    backgroundColor: isActive ? ACCENT : ACCENT_SOFT,
                  },
                }}
              >
                {pill.label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Interval: "Every [N] {units} on" */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Box sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: 500, color: TEXT_MUTED, whiteSpace: "nowrap", flexShrink: 0 }}>
          {translate("recurrence.every")}
        </Box>
        <Box sx={{ width: "44px", flexShrink: 0 }}>
          <FormTextField
            type="number"
            value={interval}
            onChange={setInterval}
            inputProps={{ min: 1, max: 365 }}
            sx={monoNumberSx}
          />
        </Box>
        <Box sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: 500, color: TEXT_MUTED, flexShrink: 0 }}>
          {unitLabel}{frequency === "WEEKLY" ? ` ${translate("recurrence.on") || "on"}` : ""}
        </Box>
      </Box>

      {/* ─── Weekly: circular day-of-week chips ──────────── */}
      {frequency === "WEEKLY" && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
          {DAY_CODES.map((code) => {
            const isActive = selectedDays.includes(code);
            const dayLabel = translate(`recurrence.daysOfWeek.${code}`);
            const initial = dayLabel ? dayLabel.charAt(0).toUpperCase() : code.charAt(0);
            return (
              <Box
                key={code}
                onClick={() => toggleDay(code)}
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  fontWeight: isActive ? 700 : 600,
                  lineHeight: "14px",
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  border: `1px solid ${isActive ? ACCENT : CARD_BORDER}`,
                  backgroundColor: isActive ? ACCENT : "#F4F6F9",
                  color: isActive ? "#FFFFFF" : "#9CA3AF",
                  boxShadow: isActive ? `${ACCENT}4D 0px 2px 5px` : "none",
                  cursor: "pointer",
                  userSelect: "none",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: ACCENT,
                    color: isActive ? "#FFFFFF" : ACCENT,
                    backgroundColor: isActive ? ACCENT : ACCENT_SOFT,
                  },
                }}
              >
                {initial}
              </Box>
            );
          })}
        </Box>
      )}

      {/* ─── ENDS section ────────────────────────────────── */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <Box sx={{
          fontFamily: dt.font,
          fontSize: "10px",
          fontWeight: 700,
          color: SECTION_LABEL,
          textTransform: "uppercase",
          letterSpacing: "1px",
          lineHeight: "12px",
        }}>
          {translate("recurrence.ends")}
        </Box>
        <Box sx={{ display: "flex", gap: "6px" }}>
          {endCards.map((card) => {
            const selected = endType === card.code;
            return (
              <Box
                key={card.code}
                onClick={() => setEndType(card.code)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "3px",
                  flex: "1 1 0%",
                  py: "9px",
                  px: "6px",
                  borderRadius: "10px",
                  border: `1.5px solid ${selected ? SELECTED_BORDER : CARD_BORDER}`,
                  backgroundColor: selected ? SELECTED_BG : CARD_BG,
                  cursor: "pointer",
                  userSelect: "none",
                  transition: "all 0.15s ease",
                  color: selected ? ACCENT : "#9CA3AF",
                  "&:hover": {
                    borderColor: selected ? SELECTED_BORDER : ACCENT_BORDER,
                  },
                }}
              >
                {card.icon}
                <Box sx={{
                  fontFamily: dt.font,
                  fontSize: "10px",
                  fontWeight: selected ? 600 : 500,
                  lineHeight: "12px",
                  color: selected ? ACCENT : TEXT_MUTED,
                }}>
                  {card.label}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* After N occurrences */}
      {endType === "count" && (
        <Box sx={{ width: "120px" }}>
          <FormTextField
            type="number"
            value={endCount}
            onChange={setEndCount}
            inputProps={{ min: 1, max: 999 }}
            sx={monoNumberSx}
          />
        </Box>
      )}

      {/* On date */}
      {endType === "date" && (
        <Box sx={{ width: "200px" }}>
          <FormDatePicker
            value={endDate}
            onChange={(d) => setEndDate(d)}
          />
        </Box>
      )}

      {/* ─── NEXT OCCURRENCES preview ────────────────────── */}
      {previewDates && previewDates.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <Box sx={{
            fontFamily: dt.font,
            fontSize: "10px",
            fontWeight: 700,
            color: SECTION_LABEL,
            textTransform: "uppercase",
            letterSpacing: "1px",
            lineHeight: "12px",
          }}>
            {translate("recurrence.preview")}
          </Box>
          <Box sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "5px",
          }}>
            {previewDates.slice(0, 5).map((date, i) => (
              <Box
                key={i}
                sx={{
                  fontFamily: dt.fontMono,
                  fontSize: "10px",
                  fontWeight: 500,
                  lineHeight: "12px",
                  color: ACCENT,
                  backgroundColor: "#D4572B0D",
                  padding: "3px 10px",
                  borderRadius: "20px",
                  border: "1px solid #D4572B1F",
                }}
              >
                {formatUiDate(date, "dayMonth")}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* ─── Summary line ────────────────────────────────── */}
      <Box sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        backgroundColor: SUMMARY_BG,
        borderRadius: "8px",
        py: "7px",
        px: "10px",
      }}>
        <AccessTimeOutlined sx={{ fontSize: 12, color: ACCENT, flexShrink: 0 }} />
        <Box sx={{
          fontFamily: dt.font,
          fontSize: "11px",
          fontWeight: 500,
          lineHeight: "14px",
          color: SUMMARY_TEXT,
        }}>
          {summaryText}
        </Box>
      </Box>
    </Box>
  );
};

// ─── RRULE parser ──────────────────────────────────────────
function parseRRule(rrule: string) {
  const defaults = {
    frequency: "MONTHLY" as Frequency,
    interval: 1,
    endType: "never" as EndType,
    endCount: 10,
    endDate: null as Date | null,
    byDay: [] as string[],
  };

  if (!rrule) return defaults;

  const parts = rrule.split(";");
  const map: Record<string, string> = {};
  parts.forEach((p) => {
    const [key, val] = p.split("=");
    if (key && val) map[key] = val;
  });

  if (map.FREQ) {
    defaults.frequency = map.FREQ as Frequency;
  }
  if (map.INTERVAL) {
    defaults.interval = parseInt(map.INTERVAL, 10) || 1;
  }
  if (map.COUNT) {
    defaults.endType = "count";
    defaults.endCount = parseInt(map.COUNT, 10) || 10;
  }
  if (map.UNTIL) {
    defaults.endType = "date";
    defaults.endDate = dayjs(map.UNTIL).toDate();
  }
  if (map.BYDAY) {
    defaults.byDay = map.BYDAY.split(",");
  }

  return defaults;
}

export default RecurrenceRulePicker;
